import type { Schemas } from '@kanos/api-client';
import { api, failure, openPdf } from '@kanos/web-core';
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

export type PortalEngagement = Schemas['PortalEngagement'];
export type OfferedContract = NonNullable<PortalEngagement['offer']>;
export type RevocationReceipt = Schemas['RevocationReceipt'];
export type RunningContract = NonNullable<PortalEngagement['contract']>;

const key = (caseId: string) => ['portal', 'intakes', caseId, 'engagement'] as const;

/** The offer of the contract of the client the intake belongs to (ADR-072 § 4). */
export const engagementQuery = (caseId: string) =>
  queryOptions({
    queryKey: key(caseId),
    queryFn: async (): Promise<PortalEngagement> => {
      const { data, error, response } = await api.GET(
        '/api/v1/portal/intakes/{caseId}/engagement',
        {
          params: { path: { caseId } },
        },
      );
      if (!data) throw failure('getOwnEngagement', error, response.status);
      return data;
    },
  });

/** An answer to the offer; afterwards the offer is read again. */
function useAnswer<T, R>(caseId: string, mutationFn: (input: T) => Promise<R>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key(caseId) }),
  });
}

type Keyed<B> = { body: B; idempotencyKey: string };
const keyed = (caseId: string, idempotencyKey: string) => ({
  params: { path: { caseId }, header: { 'Idempotency-Key': idempotencyKey } },
});

export const useAccept = (caseId: string) =>
  useAnswer(caseId, async ({ body, idempotencyKey }: Keyed<Schemas['EngagementAcceptInput']>) => {
    const { data, error, response } = await api.POST(
      '/api/v1/portal/intakes/{caseId}/engagement/accept',
      {
        ...keyed(caseId, idempotencyKey),
        body,
      },
    );
    if (!data) throw failure('acceptOwnEngagement', error, response.status);
    return data;
  });

export const useDecline = (caseId: string) =>
  useAnswer(caseId, async ({ body, idempotencyKey }: Keyed<Schemas['EngagementDeclineInput']>) => {
    const { data, error, response } = await api.POST(
      '/api/v1/portal/intakes/{caseId}/engagement/decline',
      {
        ...keyed(caseId, idempotencyKey),
        body,
      },
    );
    if (!data) throw failure('declineOwnEngagement', error, response.status);
    return data;
  });

export const useTerminate = (caseId: string) =>
  useAnswer(caseId, async ({ body, idempotencyKey }: Keyed<Schemas['OwnTerminationInput']>) => {
    const { data, error, response } = await api.POST(
      '/api/v1/portal/intakes/{caseId}/engagement/terminate',
      { ...keyed(caseId, idempotencyKey), body },
    );
    if (!data) throw failure('terminateOwnEngagement', error, response.status);
    return data;
  });

export const useRevoke = (caseId: string) =>
  useAnswer(caseId, async ({ body, idempotencyKey }: Keyed<Schemas['EngagementRevokeInput']>) => {
    const { data, error, response } = await api.POST(
      '/api/v1/portal/intakes/{caseId}/engagement/revoke',
      {
        ...keyed(caseId, idempotencyKey),
        body,
      },
    );
    if (!data) throw failure('revokeOwnEngagement', error, response.status);
    return data;
  });

/** Opens a document of the offer - the record of the acceptance among them - as PDF. */
export const useOpenDocument = (caseId: string) =>
  useMutation({
    mutationFn: async ({ document, version }: { document: string; version: number }) => {
      const { data, error, response } = await api.GET(
        '/api/v1/portal/intakes/{caseId}/engagement/versions/{version}/documents/{document}',
        { params: { path: { caseId, document, version } }, parseAs: 'blob' },
      );
      if (!data) throw failure('downloadOwnEngagementDocument', error, response.status);
      openPdf(data, `${document.toLowerCase()}-fassung-${String(version)}.pdf`);
    },
  });
